
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import { BARCODE_SCAN } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { promptVideoPermission } from '@/utils/helpers'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import 'cropperjs/dist/cropper.css'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { EBCRType } from 'smartbarcode-web-core/src/utils/enums/index'
import { IListStringItem, IProject } from 'smartbarcode-web-core/src/utils/types'
import { mixins, Options } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'

// Mixins
@Options({
  components: {
    BarcodeSymbologiesSelection,
    IconSwitch,
    SelectionCameraPopup,
  },
  emits: ['update:modelValue', 'update:confirm'],
  inheritAttrs: false,
  name: 'ScanComponent',
})
export default class ListStringScanComponent extends mixins(BarcodeReaderMixin) {
  @Prop({ type: Boolean, default: false }) readonly modelValue!: boolean
  @Prop({ type: Array, default: [] }) readonly scannedValues!: IListStringItem[]
  @Prop({ default: EBCRType.DEFAULT }) customBarcodECameraReaderType!: EBCRType // This will override customBarcodECameraReaderType from mixins
  @Prop({ type: [String, Number] }) readonly initialValue?: string | number
  @Prop({ type: Number }) readonly minValue?: number
  @Prop({ type: Number }) readonly maxValue?: number
  codeReader: BrowserMultiFormatReader | null = new BrowserMultiFormatReader(
    undefined,
    BARCODE_SCAN.timeBetweenScansMillis as number
  )

  _barcodeDataCapture: BarcodeCapture | undefined
  loading = true
  video: HTMLVideoElement | null = null
  pictureWidth = 320
  pictureHeight = 320
  isCaptured = false
  showItemsListDialog = false

  scanMode: 'add' | 'remove' = 'add'

  confirm() {
    this.$emit('update:confirm', this.valueCode.trim(), this.scanMode)
  }

  showItemsList() {
    if (this.scannedValues.length > 0) {
      this.showItemsListDialog = true
    }
  }

  onDialogClosing() {
    this.$emit('update:modelValue', false)
  }

  get syncedModel() {
    return this.modelValue
  }

  set syncedModel(value: boolean) {
    this.$emit('update:modelValue', value)
  }

  defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.codeReader = codeReader
    const tempResult: { [key: string]: unknown } = { ...result }
    if (!tempResult?.text) {
      return
    }
    this.handleScanResult((tempResult.text as string) || '')
  }

  scanditDataResult(symbology: Barcode) {
    if (!symbology) return
    this.handleScanResult((symbology?.data as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    if (result.length < 1) return
    const firstDetectedBarcode = result[0]?.rawValue || ''
    this.handleScanResult(firstDetectedBarcode)
  }

  handleScanResult(plainText: string) {
    this.valueCode = plainText
    // Automatically call confirm when a new value is scanned
    if (plainText.trim()) {
      this.confirm()
    }
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get validationError(): string {
    const length = this.scannedValues.length

    if (this.minValue !== undefined && length < this.minValue) {
      return this.$t('minimum_items_required', { minLength: this.minValue })
    }

    if (this.maxValue !== undefined && length > this.maxValue) {
      return this.$t('maximum_items_required', { maxLength: this.maxValue })
    }

    return ''
  }

  get hasValidationError(): boolean {
    return !!this.validationError
  }

  async mounted() {
    // Disable multiple scan for immediate confirmation
    this.isShowMultipleScan = false
    this.isMultipleScan = false

    // Set initial value if provided
    if (this.initialValue) {
      this.valueCode = this.initialValue.toString()
    }

    await this.initCameraSelection()
    if (this.isCustomVideoReader) {
      await promptVideoPermission()
    }

    await this.setupCameraSetting()
  }

  get isCustomVideoReader() {
    return !(this.customBarcodECameraReaderType === EBCRType.SCANDIT)
  }

  async setupCameraSetting() {
    switch (this.barcodeReaderType as EBCRType) {
      case EBCRType.SCANDIT:
        await this.initScanditReader()
        break
      case EBCRType.BARCODE_DETECTION:
        await this.initBarcodeReaderPolyFill()
        break
      default:
        await promptVideoPermission()
        await this.initZxingReader()
        break
    }
  }

  get isScanditReader() {
    return this.customBarcodECameraReaderType === EBCRType.SCANDIT
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 1000 },
            height: { ideal: 1000 },
            frameRate: {
              ideal: 30,
              max: 60,
            },
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }

    // For single scan mode, just confirm immediately
    this.valueCode = plainText
    this.confirm()
  }
}
